import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { GetProfileAction } from '@tsin-core/actions/auth.action';
import { ActivityTrackerService } from '@tsin-core/services/common/activity-tracker.service';
import { BreadcrumbService } from '@tsin-core/services/common/breadcrumb.service';
import { environment } from '@tsin-env/environment';
import { MainHeaderComponent } from '@tsin-shared/components/main-header/main-header.component';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-management',
  imports: [AsyncPipe, RouterModule, MainHeaderComponent, ConfirmDialog, BreadcrumbModule],
  templateUrl: './management.layout.html',
  styleUrl: './management.layout.css',
  providers: [ConfirmationService],
})
export class ManagementLayout {
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);
  private store = inject(Store);
  private activityTracker = inject(ActivityTrackerService);
  private breadcrumbSvc = inject(BreadcrumbService);

  readonly breadcrumbs$: Observable<MenuItem[]> = this.breadcrumbSvc.breadcrumbs$;
  readonly breadcrumbHome: MenuItem = {
    icon: 'pi pi-home',
    label: 'Management',
    routerLink: '/management/all',
  };

  private inactivityTimeout?: any;
  private countdownInterval?: any;
  private countdownSeconds = 120; // 2 minutes
  private activitySubscription?: Subscription;

  private lastActivityTime = Date.now();
  private readonly TOKEN_REFRESH_THRESHOLD = 13 * 60 * 1000; // 13 minutes
  private readonly TOKEN_EXPIRY_TIME = 15 * 60 * 1000; // 15 minutes

  onServerActivity() {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;

    if (timeSinceLastActivity >= this.TOKEN_REFRESH_THRESHOLD) {
      this.refreshToken();
      this.lastActivityTime = now;
    }
  }

  private refreshToken() {
    // Call your token refresh endpoint
    // This will be triggered by the interceptor when a server call is made
    // console.log('Token refresh triggered');
  }
  ngOnInit() {
    this.setupInactivityTimer();
    this.listenToUserActivity();
  }

  ngOnDestroy() {
    this.clearTimers();
    this.removeActivityListeners();
  }

  private setupInactivityTimer() {
    this.clearTimers();
    this.inactivityTimeout = setTimeout(() => {
      this.showInactivityWarning();
    }, this.TOKEN_REFRESH_THRESHOLD); // 13 minutes
  }

  private showInactivityWarning() {
    this.countdownSeconds = 120;

    this.confirmationService.confirm({
      header: 'Inactivity Warning',
      message: `You will be logged out in ${this.countdownSeconds} seconds due to inactivity.`,
      acceptLabel: 'Stay Logged In',
      rejectVisible: false,
      accept: () => {
        this.clearTimers();
        this.setupInactivityTimer();
        this.getProfile();
      },
    });

    this.countdownInterval = setInterval(() => {
      this.countdownSeconds--;

      if (this.countdownSeconds > 0) {
        this.confirmationService.confirm({
          header: 'Inactivity Warning',
          message: `You will be logged out in ${this.countdownSeconds} seconds due to inactivity.`,
          acceptLabel: 'Stay Logged In',
          rejectVisible: false,
          accept: () => {
            this.clearTimers();
            this.getProfile();
            this.setupInactivityTimer();
          },
        });
      } else {
        this.clearTimers();
        window.location.href = environment.userMgtUrl;
      }
    }, 1000);
  }

  private listenToUserActivity() {
    // Subscribe to server activity from the JWT interceptor
    this.activitySubscription = this.activityTracker.activity$.subscribe(() => {
      this.resetTimer();
    });
  }

  private removeActivityListeners() {
    // Unsubscribe from server activity
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
    }
  }

  private resetTimer() {
    if (!this.countdownInterval) {
      this.setupInactivityTimer();
    }
  }

  private clearTimers() {
    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  private getProfile() {
    this.store.dispatch(new GetProfileAction());
  }
}
